// src/encounter/risk/lib/steadi-follow-up.ts
//
// PROD-3128 (Andrew, 8 Oct, answer (b)) · frame v2 ONLY. Under "Fall risk present: Yes", every template asks the CDC
// STEADI three key questions, the same set the adult template authors, the way Pain opens its details under "Pain
// present: Yes". A Morse-era template ("Dr Johny Template") has only the switch and the Morse questions, which are an
// old record's read-only history since the 7 Oct 2026 licence ruling, so "Yes" opened nothing.
//
// WHAT IS ASKED · `STEADI_SECTION_FHIR`, the template pack's own `steadiSection()` (rule P13; a test pins the two
// equal), offered only when the template's Fall group has a "Fall risk present" item and the template authors no STEADI
// item anywhere. Never for a minor: the pack removes STEADI for minors (R-LIC-2, "STEADI is for adults 65 and older"),
// so a patient under 18 by the record's date of birth gets none. Morse is never opened.
//
// WHERE THE ANSWERS LIVE · the Fall record, as Flutter's own Fall screen writes them: the managed line "CDC STEADI key
// questions: …" in its note (`projectFreeScreens`, with `steadiSetsPresent: false`, so the clinician's own "Fall risk
// present" stays `isPresent`). They are read back from that line (`steadiAnswersFromNote`) and never sent in the
// QuestionnaireResponse: the template has no such items, and the Fall record is the one place for the fact (G7).
import { fallAssessmentParts, type QItem, type QRAnswerItem } from '@scripsteam/scrips-signal-ds';
import type { FhirQuestionnaireItem } from '@/types/template-studio-api';
import { ageOnDate } from '../../lib/chart-population';
import { fhirToDsQuestionnaire } from '../../lib/fhir-to-ds-questionnaire';
import { freeScreenScope, riskBridgeScope, steadiNoteParts, STEADI_LINKS } from './risk-qr-bridge';

const HL7_YN = (code: 'Y' | 'N', display: 'Yes' | 'No') => ({ system: 'http://terminology.hl7.org/CodeSystem/v2-0136', code, display });
const yn = () => [HL7_YN('Y', 'Yes'), HL7_YN('N', 'No')].map((valueCoding) => ({ valueCoding }));

/** `scripts/lib/template-pack.mjs` `steadiSection()`, verbatim (steadi-follow-up.test.ts pins the two equal). */
export const STEADI_SECTION_FHIR: FhirQuestionnaireItem = {
  linkId: 'risk-steadi',
  text: 'Fall risk — CDC STEADI three key questions (adults 65 and older; at risk if YES to any; CDC 2019)',
  type: 'group',
  item: [
    { linkId: 'risk-steadi-unsteady', text: 'Feels unsteady when standing or walking?', type: 'choice', answerOption: yn() },
    { linkId: 'risk-steadi-worries', text: 'Worries about falling?', type: 'choice', answerOption: yn() },
    { linkId: 'risk-steadi-fallen', text: 'Has fallen in past year?', type: 'choice', answerOption: yn() },
    { linkId: 'risk-steadi-fall-count', text: 'If fallen in the past year: how many times?', type: 'string' },
    { linkId: 'risk-steadi-injured', text: 'If fallen in the past year: were you injured?', type: 'choice', answerOption: yn() },
  ],
};

let group: QItem | undefined;
/** The STEADI group as the note's renderer reads it (the same adapter every served template goes through). */
export function steadiFollowUpGroup(): QItem {
  group ??= fhirToDsQuestionnaire({ resourceType: 'Questionnaire', item: [STEADI_SECTION_FHIR] }).item[0];
  return group;
}

/** STEADI is for adults (R-LIC-2): a patient under 18 on the record's date of birth gets none. Unknown age → offered. */
export const STEADI_MIN_AGE = 18;

/**
 * The STEADI follow-up this visit's template needs, or undefined: the Fall group asks "Fall risk present", the template
 * authors no STEADI item, and the patient is not a minor.
 */
export function steadiFollowUpFor(
  items: readonly QItem[] | undefined,
  patient: { birthDateIso?: string | null; today?: string } = {},
): QItem | undefined {
  const { fall } = riskBridgeScope(items);
  if (!fall || !fallAssessmentParts(fall).present) return undefined;
  if (Object.keys(freeScreenScope(items).steadi).length > 0) return undefined;
  const age = patient.birthDateIso ? ageOnDate(patient.birthDateIso, patient.today) : undefined;
  if (age !== undefined && age < STEADI_MIN_AGE) return undefined;
  return steadiFollowUpGroup();
}

/** The follow-up's linkIds: answered on screen, saved only to the Fall record, never in the QuestionnaireResponse. */
export const STEADI_FOLLOW_UP_LINKS: ReadonlySet<string> = new Set(Object.values(STEADI_LINKS));

/** The Fall record's managed STEADI line as the follow-up's answers (the option code for a Yes / No, the count's text). */
export function steadiAnswersFromNote(steadi: QItem, note: string | undefined): QRAnswerItem[] {
  const parts = steadiNoteParts(note);
  if (!parts) return [];
  const out: QRAnswerItem[] = [];
  for (const key of Object.keys(STEADI_LINKS) as (keyof typeof STEADI_LINKS)[]) {
    const text = parts[key];
    const item = steadi.item?.find((i) => i.linkId === STEADI_LINKS[key]);
    if (text === undefined || !item) continue;
    if (item.type === 'string') {
      out.push({ linkId: item.linkId, value: text });
      continue;
    }
    const code = item.answerOption?.find((o) => o.valueCoding?.display === text)?.valueCoding?.code;
    if (code) out.push({ linkId: item.linkId, value: code });
  }
  return out;
}
